import { faCalendarCheck } from "@fortawesome/pro-light-svg-icons"
import {
  JoonUIColor,
  Loader,
  SPACING,
  Typography,
  Button,
} from "@joonapp/web-shared"
import { FlexBox } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useMemo } from "react"

import { useSessionBookingModalStore } from "./hooks/useSessionBookingModalStore"
import SessionBillingPeriodGroup from "./SessionBillingPeriodGroup"
import SessionModal from "./SessionModal"
import SessionSlot from "./SessionSlot"
import useUserRole from "../../../hooks/useUserRole"
import {
  useSessionBillingPeriodsQuery,
  useSessionBookingsQuery,
  useUpcomingSessionsQuery,
} from "../../../networking/sessions/queries"
import { localStorageItems } from "../../../util/storage"
import useCurrentClient from "../hooks/useCurrentClient"

const SessionsTab = () => {
  const {
    data: billingPeriods,
    refetch: refetchBillingPeriods,
    isLoading: isLoadingBillingPeriods,
    isFetching: isFetchingBillingPeriods,
  } = useSessionBillingPeriodsQuery()
  const {
    data: bookings,
    refetch: refetchBookings,
    isLoading: isLoadingBookings,
    isFetching: isFetchingBookings,
  } = useSessionBookingsQuery()
  const { refetch: refetchUpcomingSessions } = useUpcomingSessionsQuery()
  const { currentCoachingGroup } = useCurrentClient()
  const { isClientOfOtherCoach } = useUserRole()
  const { isOpen: isSessionBookingModalOpen } = useSessionBookingModalStore()

  const { pastSessions, makeUpSessions } = useMemo(() => {
    const currentPeriodsSessionIds = billingPeriods?.flatMap((period) =>
      period.bookings?.map((booking) => booking.id)
    )

    const isSessionOutsidePeriods = (booking: any) =>
      !currentPeriodsSessionIds?.includes(booking.id)

    const pastSessions = bookings?.filter(
      (booking) =>
        (isSessionOutsidePeriods(booking) || !!booking.missed_session_id) &&
        dayjs(booking.start_datetime).isBefore(dayjs())
    )

    const makeUpSessions = bookings?.filter(
      (booking) =>
        !!booking.missed_session_id &&
        dayjs(booking.start_datetime).isAfter(dayjs())
    )

    return { pastSessions, makeUpSessions }
  }, [billingPeriods, bookings])

  const handleManageAvailability = () => {
    window.open("https://app.cal.com/availability", "_blank")
  }

  useEffect(() => {
    if (isSessionBookingModalOpen) return

    if (!!localStorage.getItem(localStorageItems.needsToRefreshBookings)) {
      localStorage.removeItem(localStorageItems.needsToRefreshBookings)
      refetchBillingPeriods()
      refetchBookings()
      refetchUpcomingSessions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionBookingModalOpen, refetchBillingPeriods])

  if (isLoadingBillingPeriods || isLoadingBookings)
    return (
      <FlexBox
        direction="column"
        align="center"
        justify="center"
        style={{ height: "50%" }}
      >
        <Loader size={32} color={JoonUIColor.icon.accent} />
      </FlexBox>
    )

  if (!currentCoachingGroup) return <></>

  return (
    <FlexBox
      direction="column"
      wrap={false}
      gap={SPACING.space6}
      style={{
        padding: SPACING.space6,
        maxHeight: "calc(100vh - 100px)",
        overflowY: "auto",
      }}
    >
      <div className="flex flex-row items-center justify-between w-full">
        {!isClientOfOtherCoach && (
          <Button
            text="Manage my availability"
            buttonType="secondary"
            icon={faCalendarCheck}
            onClick={handleManageAvailability}
            size="small"
          />
        )}

        {(isFetchingBillingPeriods || isFetchingBookings) && (
          <div className="flex flex-row items-center gap-2">
            <Loader size={16} color={JoonUIColor.icon.neutral} thickness={2} />
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              Refreshing...
            </Typography>
          </div>
        )}
      </div>
      {makeUpSessions && makeUpSessions?.length > 0 && (
        <FlexBox direction="column" gap={SPACING.space2}>
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.text.secondary}
          >
            MAKE-UP SESSION
          </Typography>
          {makeUpSessions?.map((session) => (
            <SessionSlot
              session={session}
              key={session.id}
              billingPeriod={billingPeriods?.find((period) =>
                period.bookings?.find((booking) => booking.id === session.id)
              )}
            />
          ))}
        </FlexBox>
      )}

      <FlexBox direction="column" gap={SPACING.space6}>
        {billingPeriods?.map((period, i) => {
          return (
            <SessionBillingPeriodGroup
              period={period}
              isCurrentCycle={i === 0}
              key={i}
            />
          )
        })}
      </FlexBox>
      {pastSessions && pastSessions?.length > 0 && (
        <FlexBox direction="column" gap={SPACING.space2}>
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.text.secondary}
          >
            PAST SESSIONS
          </Typography>
          {pastSessions?.map((session) => (
            <SessionSlot session={session} key={session.id} />
          ))}
        </FlexBox>
      )}
      <SessionModal />
    </FlexBox>
  )
}

export default SessionsTab
